// ============================================================
// What the composer's line asks for, before anything is written.
//
// The decision half of useChat's send (v8.363.3): the locks, the parse,
// the in/out-of-character toggles and what a /roll becomes. The hook
// keeps the clock, the state and the write.
//
// Pure except `rollForLine`, which rolls dice - the one call engine code
// may make to the random source (CLAUDE.md, gotcha 5).
// ============================================================
import { parseDiceExpression, rollDiceExpression } from '../dice';
import { buildDiceRollNarrative } from '../characteristicTests';
import type { NarrativeEntry } from '../../types';
import { parseComposer } from './parse';
import type { ParsedLine } from './parse';
import type { ChatChannel, ConversationKey, Person, RollPayload, SpeakAs } from './types';

/** What a line does: refused, a mask toggle, or a line to send. */
export type LinePlan =
  | { kind: 'refuse'; error: string }
  | { kind: 'mask'; inCharacter: boolean; error: string | null }
  | { kind: 'send'; parsed: ParsedLine };

export interface PlanContext {
  isLocked: (feature: string) => boolean;
  /** Only the GM announces (v9.7.0). */
  isGm?: boolean;
  people: Person[];
  channels: ChatChannel[];
  me: string;
  active: ConversationKey;
  actor: SpeakAs | null;
}

const refuse = (error: string): LinePlan => ({ kind: 'refuse', error });

/** A whisper, or a plain line while a private vox is the active chip. */
function isPrivate(parsed: ParsedLine, active: ConversationKey): boolean {
  return parsed.kind === 'whisper' || (parsed.kind !== 'group' && active.startsWith('dm:'));
}

/** /ic puts the mask on (and says so when there is no one to wear); /ooc takes it off. */
function maskPlan(kind: 'ic' | 'ooc', actor: SpeakAs | null): LinePlan {
  if (kind === 'ooc') return { kind: 'mask', inCharacter: false, error: null };
  return {
    kind: 'mask',
    inCharacter: true,
    error: actor ? null : 'You are acting as nobody - pick a combatant first.',
  };
}

/**
 * Decide what the composer's line does. The whole vox lock is checked
 * before the line is even parsed; the private-vox lock only once it is
 * known to be private.
 */
export function planLine(raw: string, ctx: PlanContext): LinePlan {
  if (ctx.isLocked('chat')) return refuse('The GM has closed the vox.');
  const parsed = parseComposer(raw, { people: ctx.people, channels: ctx.channels, me: ctx.me });
  if (parsed.kind === 'error') return refuse(parsed.message);
  if (parsed.kind === 'ic' || parsed.kind === 'ooc') return maskPlan(parsed.kind, ctx.actor);
  const refusal = refusalOf(parsed, ctx);
  return refusal ? refuse(refusal) : { kind: 'send', parsed };
}

/** Why this line may not be sent by this person, or null when it may. */
function refusalOf(parsed: ParsedLine, ctx: PlanContext): string | null {
  if (parsed.kind === 'announce') return ctx.isGm ? null : 'Only the GM announces.';
  if (isPrivate(parsed, ctx.active) && ctx.isLocked('chatWhisper')) return 'The GM has closed private vox.';
  return null;
}

/** A rolled /roll: a log entry for the table, or the payload a chat line carries. */
export type RolledLine = { narrative: NarrativeEntry } | { roll: RollPayload };

/**
 * Roll a /roll's dice, or null when the expression has none.
 *
 * In Vox - all it becomes the same dice-roll log entry the dice tray
 * posts, for everyone; anywhere else it stays a chat line with the roll
 * attached, because the log has no audience. The individual dice are
 * flattened - a chat line shows `expr = total` with them on hover.
 */
export function rollForLine(expr: string, active: ConversationKey, ts: number): RolledLine | null {
  if (parseDiceExpression(expr).length === 0) return null;
  const result = rollDiceExpression(expr);
  if (active === 'table') {
    const structured = buildDiceRollNarrative(expr, result);
    return { narrative: { type: 'dice-roll', text: structured.summary, structured, timestamp: ts } };
  }
  return { roll: { expr, total: result.total, results: result.results.flatMap((g) => g.rolls ?? []) } };
}
